// THE OWNER'S SIGNATURE, HELD BY THIS TAB AND NOWHERE ELSE (WEB-PARITY-6, R221; WALK-FIX-1).
//
// The app keeps a signature on the phone, per appeal, and draws it into the PDF at export ("Your signature is added to
// this letter's PDF only. It isn't saved to your account or reused for any other appeal", SignatureEditor.swift:40). The
// website keeps it in this tab, per appeal, and sends it in the request each time a PDF is made (`v2GenerateLetter` op
// `pdf`, and op `email` when the emailed copy is on); the server draws it and keeps it nowhere.
//
// IN THE TAB'S `sessionStorage` UNTIL THE SIGNED PDF IS MADE (WALK-FIX-1, R236; the coordinator's ruling,
// 2026-10-05). WEB-PARITY-6 held it in memory only, so a reload forgot it — and Tyler's walk lost it exactly so: he typed
// a URL, the page reloaded, the card still said "Signed", and his PDF went out with a blank line. So the signature is
// now ALSO kept in this tab's `sessionStorage`, keyed by appeal, which:
//   - survives a reload of the same tab, and dies with the tab (the browser clears it when the tab closes);
//   - is never `localStorage` (which outlives the tab, readable by the next person at a shared computer), never a cookie
//     (which would ride on every request), and never sent anywhere but the PDF requests above;
//   - is REMOVED from storage the moment a signed PDF comes back (`releaseStoredSignature`), so the disk holds it only
//     between signing and the export it was made for; the page keeps it in memory for the rest of its life, as before.
// `test/walkFix1.test.tsx` checks it survives a reload, reaches no other storage and no other request, and is gone from
// storage after the signed export; `test/letterReview.test.tsx` still checks a signed export leaves no storage behind.

import { useSyncExternalStore } from "react";

export interface HeldSignature {
  /** A PNG, base64 — what `signature.pngBase64` carries. */
  readonly pngBase64: string;
  /** In pixels. */
  readonly width: number;
  readonly height: number;
}

const held = new Map<string, HeldSignature>();
const listeners = new Set<() => void>();

/** The tab's own storage key for one appeal's signature. */
export const SIGNATURE_KEY_PREFIX = "proppeal.signature.";
const keyOf = (appealId: string): string => `${SIGNATURE_KEY_PREFIX}${appealId}`;

/** The tab's `sessionStorage`, or null where there is none (a private window that refuses it, a server render). Every
 *  touch is guarded: a storage that throws costs the reload, never the signature in memory. */
function tabStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function stored(appealId: string): HeldSignature | null {
  try {
    const raw = tabStorage()?.getItem(keyOf(appealId)) ?? null;
    if (raw === null) return null;
    const v = JSON.parse(raw) as Partial<HeldSignature>;
    if (typeof v.pngBase64 !== "string" || v.pngBase64 === "" || typeof v.width !== "number" || typeof v.height !== "number") return null;
    return { pngBase64: v.pngBase64, width: v.width, height: v.height };
  } catch {
    return null;
  }
}

function store(appealId: string, signature: HeldSignature | null): void {
  try {
    const s = tabStorage();
    if (s === null) return;
    if (signature === null) s.removeItem(keyOf(appealId));
    else s.setItem(keyOf(appealId), JSON.stringify(signature));
  } catch {
    // Full or refused: the signature stays in memory for this page, which is what WEB-PARITY-6 always had.
  }
}

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** This appeal's signature: the page's, or — after a reload — the tab's stored one, read once into the page's. */
export function signatureFor(appealId: string): HeldSignature | null {
  const inPage = held.get(appealId);
  if (inPage !== undefined) return inPage;
  const fromTab = stored(appealId);
  if (fromTab !== null) held.set(appealId, fromTab);
  return fromTab;
}

export function holdSignature(appealId: string, signature: HeldSignature): void {
  held.set(appealId, signature);
  store(appealId, signature);
  notify();
}

export function forgetSignature(appealId: string): void {
  const had = held.delete(appealId);
  store(appealId, null);
  if (had) notify();
}

/** A signed PDF came back: the tab's stored copy goes, and the page keeps its own (WALK-FIX-1). */
export function releaseStoredSignature(appealId: string): void {
  store(appealId, null);
}

/** Test-only: what a reload of this tab does — the page's memory is gone, the tab's storage is not. */
export function reloadTab(): void {
  held.clear();
  notify();
}

/** Test-only: a fresh tab — nothing in the page, nothing in the tab's storage. */
export function forgetAllSignatures(): void {
  held.clear();
  try {
    const s = tabStorage();
    if (s !== null) for (const k of Object.keys(s)) if (k.startsWith(SIGNATURE_KEY_PREFIX)) s.removeItem(k);
  } catch {
    // nothing stored
  }
  notify();
}

/** This appeal's signature, re-read whenever one is saved or cleared. */
export function useSignature(appealId: string): HeldSignature | null {
  return useSyncExternalStore(subscribe, () => signatureFor(appealId), () => null);
}
